import { type LinearConfiguratorState } from "~/features/configurator/Linear/redux/types";
import { type LinearConfiguration } from "~/features/configurator/Linear/models";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { type LinearConfigurationDTO } from "~/features/configurator/Linear/dtos";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { transform } from "~/features/configurator/Linear/linearDataTransformer";
import {
  type ConfiguratorApiCall,
  prepareCommonConfiguratorData,
  type ShaftConfigurationData,
} from "~/features/configurator/common/commonConfiguratorApi";
import {
  getConfiguratorFamilyFromProductCode,
  getConfiguratorTypeFromProductCode,
} from "~/domains/configurator/types/ConfiguratorType";

import { type PriceInfo } from "~/domains/pricing/api/PriceInfo";
import instance from "~/utils/i18n";
import { XML_TRANSLATION_SHEETS } from "~/features/configurator/common/useXmlConfiguratorTranslations";

type ShaftConfiguratorApiCall = ConfiguratorApiCall & Required<Pick<ConfiguratorApiCall, "shaftConfiguratorData">>;

export function prepareCommonShaftData(
  linearState: LinearConfiguratorState,
  priceInfo?: PriceInfo | null,
): ShaftConfiguratorApiCall {
  const data = prepareCommonConfiguratorData(linearState, priceInfo);

  const configuratorFamily = getConfiguratorFamilyFromProductCode(linearState.productCode);
  const t = (key: string) =>
    instance.t(key, { ns: XML_TRANSLATION_SHEETS[linearState.type!].sheetName, context: configuratorFamily });

  const shaftData: ShaftConfigurationData = {
    // currentStepNumber is 1 based
    currentStepNumber: linearState.currentStep + 1,
    highestVisitedStepNumber: linearState.highTideStep + 1,

    description: linearState.description,
    mediaUrl: t(linearState.productImageUrl),
  };

  data.shaftConfiguratorData = shaftData;

  return data as ShaftConfiguratorApiCall;
}

const apiUrl = resolveApiUrl("/shaft/getconfig");

export function sendShaft(apiData: ConfiguratorApiCall): Promise<LinearConfiguration> {
  return fetch(apiUrl, {
    body: JSON.stringify(apiData),
    cache: "no-cache",
    credentials: "same-origin",
    method: "POST",
    mode: "cors",
    headers: {
      dataType: "json",
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
    redirect: "follow",
  }).then((result) => {
    if (result.ok) {
      return result
        .json()
        .then((data: LinearConfigurationDTO) =>
          transform(data, getConfiguratorTypeFromProductCode(apiData.kbKey.productCode)!, undefined),
        );
    } else {
      console.error(`Error sending linear update: ${result.status}`);
      throw new Error(result.status + "");
    }
  });
}
